/**
 * Shared CSRF interceptor for module API clients.
 *
 * Reads the `csrf_token` cookie (set by the backend at login) and attaches it
 * as the `X-CSRF-Token` header on every mutating request (POST/PUT/PATCH/DELETE).
 *
 * Usage:
 *   import { attachCsrfInterceptor } from '@/modules/shared/csrfInterceptor';
 *   attachCsrfInterceptor(axiosInstance);
 */
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function attachCsrfInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const method = (config.method || 'get').toUpperCase();
      if (MUTATING_METHODS.has(method)) {
        const csrf = getCsrfToken();
        if (csrf) {
          config.headers['X-CSRF-Token'] = csrf;
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );
}
